import { supabase } from '../auth/supabase'

/**
 * "A chore has been put on your list" — the event-driven half of notifications.
 *
 * `docs/architecture-review.md` §2 separates this from the daily reminder before
 * either existed, and the distinction still holds: the reminder is a cron digest
 * that wakes every five minutes and asks who wants a nudge, while this fires
 * because somebody tapped *Ajouter*. They are two mechanisms, and trying to make
 * one do both is how the cron ends up sending things nobody asked for.
 *
 * **The client never names an endpoint.** It sends a task id; the function
 * resolves that to a member, and the member to their browsers, on the server.
 * §2 states the rule before the function existed and it is the one line here
 * worth memorising: a function that pushes to whatever endpoint it is handed is
 * an open relay wearing your VAPID key.
 */

/**
 * Tell the assignee's devices about a chore that was just put on their list.
 *
 * Fire-and-forget by construction: the store has already written the chore and
 * marked it dirty by the time this is called, so a failure here costs a
 * notification and never the chore. That ordering is not incidental — awaiting a
 * network call from a click handler is precisely the shape the draft's
 * `pushCompletion` had, and it dropped the thing the user did whenever the
 * request failed.
 *
 * Resolves to the reason it did nothing, or `null` when a notification was
 * accepted. The caller decides whether that is worth a line on screen; nothing
 * here writes to the console, because a `.catch(console.error)` on a path like
 * this hides exactly the failure the user needed to see.
 */
export async function notifyTaskAssigned(taskId: string): Promise<string | null> {
  const client = supabase
  if (!client || !taskId) return 'not-configured'

  try {
    const { error } = await client.functions.invoke('send-task-assigned', { body: { taskId } })
    if (error) return error instanceof Error ? error.message : String(error)
    return null
  } catch (error) {
    // Offline, or the function is not deployed yet. The chore is already saved
    // and will sync; the person will see it the next time they open the app,
    // which is the behaviour this feature degrades to and not a broken state.
    return error instanceof Error ? error.message : String(error)
  }
}
